import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ClosureDTO,
  createClosure,
  updateClosure,
  ClientContactDTO,
  saveActivityLog,
  LogEntityType,
  LogContextType,
  LogAction,
  createContractPlacement,
} from 'wisestep-api';
import { ControlledTextArea, Label, WsButton } from 'wisestep-base';
import { object } from 'yup';

import ClientContactAutoComplete from '../../../components/clientContactAutoComplete';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import {
  getAvEditAddDesUiName,
  activityLogDTOHelper,
  getSubmissonConversionUIName,
} from '../../../features/activityLog/ActivityHelper';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import CreateNewClientContactDialog from '../../jobs/post/components/CreateNewClientContactDialog';

const validationSchema = object().shape({});

const isValidObj = (str: string): boolean => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

interface IComponentProps {
  setClosureDTO: (data: ClosureDTO) => void;
  currentClosureDTO: ClosureDTO;
  onCancel: () => void;
  setDirtyFieldsValue: (data: any) => void;
  getDirtyFieldsValue: any;
  getCandidateFromSubmissionStatus?: any;
  placementType: string;
}

const ClosureRecruiterDetails = ({
  setClosureDTO,
  currentClosureDTO,
  onCancel,
  getCandidateFromSubmissionStatus,
  getDirtyFieldsValue,
  placementType,
}: IComponentProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const navigate = useNavigate();
  const [openClientContactDialog, setOpenClientContactDialog] = useState<boolean>(false);
  const [companyContactInput, setCompanyContactInput] = useState<string>('');

  const getDefaultValues = (): ClosureDTO => {
    const responseDTO = { ...currentClosureDTO };
    responseDTO.accountManager = isValidObj(currentClosureDTO.accountManager)
      ? JSON.parse(currentClosureDTO.accountManager)
      : '';
    responseDTO.clientRecruiterOne = currentClosureDTO.clientRecruiterOne ?? '';
    return responseDTO;
  };

  const methods = useForm<any>({
    defaultValues: getDefaultValues(),
    resolver: yupResolver(validationSchema),
  });
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { dirtyFields },
  } = methods;
  const companyName = watch('clientName') ?? '';
  const clientRecruiterOne = watch('clientRecruiterOne');
  const clientRecruiterTwo = watch('clientRecruiterTwo');

  useEffect(() => {
    if (isEmpty(companyName?.trim())) {
      setValue('clientRecruiterOne', '');
      setValue('clientRecruiterTwo', '');
    }
  }, [clientRecruiterOne, clientRecruiterTwo, companyName, setValue]);

  const onClientContactCreateFromDialog = (selectedClientRecruiter: ClientContactDTO) => {
    const fullName = selectedClientRecruiter?.firstName
      .concat(' ' + selectedClientRecruiter?.lastName)
      .trim();

    setValue('clientRecruiterOne', `${fullName} - ${selectedClientRecruiter.emailAddress}`);
    setValue('clientContactId', selectedClientRecruiter?.id ?? '');
    setValue('clientRecruiterName', fullName);
    setOpenClientContactDialog(false);
  };

  const onSubmit = async (data: ClosureDTO) => {
    data.accountManager = data.accountManager ? JSON.stringify(data.accountManager) : '';
    if (data.placementStatusId) setClosureDTO(data);
    try {
      const avPayload = { ...activityLogDTOHelper };
      const avChangedValueViaDirtyFileds = Object.keys({ ...getDirtyFieldsValue, ...dirtyFields })
        .toString()
        .split(',');

      let response;
      if (data.id) {
        response = await updateClosure(data);
        avPayload.action = LogAction.Edit;
        const avEmptyEntityfields = Object.keys(
          Object.entries(currentClosureDTO).reduce((acc, [k, v]) => {
            if (v === '' || v === null) acc[k] = v;
            return acc;
          }, {})
        );
        avPayload.description = `${getAvEditAddDesUiName(
          'Placement',
          avEmptyEntityfields,
          avChangedValueViaDirtyFileds
        )} under Placement`;
        avPayload.parentEntitiyType = LogContextType.Placement;
        avPayload.parentEntitiyId = data.id;
        avPayload.entityId = data.id;
      } else {
        if (placementType === 'PERMANENT') {
          response = await createClosure(data);
        } else if (placementType === 'CONTRACT') {
          response = await createContractPlacement(data);
        }
        avPayload.action = LogAction.Add;
        avPayload.entityId = response?.data?.value;

        if (getCandidateFromSubmissionStatus) {
          const editObjectInOnversion: string[] = [];
          const candidateEditList = {
            clientName: getCandidateFromSubmissionStatus.companyName as string,
            candidateName: getCandidateFromSubmissionStatus.name as string,
            candidateEmail: getCandidateFromSubmissionStatus.email as string,
            candidateContact: getCandidateFromSubmissionStatus.phoneNumber as string,
            positionId: getCandidateFromSubmissionStatus.jobId as string,
          };

          const candidateDataEditList = {
            clientName: data.clientName as string,
            candidateName: data.candidateName as string,
            candidateEmail: data.candidateEmail as string,
            candidateContact: data.candidateContact as string,
            positionId: data.positionId as string,
          };

          if (candidateEditList.clientName !== candidateDataEditList.clientName) {
            editObjectInOnversion.push('Client Name');
          }
          if (candidateEditList.candidateName !== candidateDataEditList.candidateName) {
            editObjectInOnversion.push('Candidate Name');
          }
          if (candidateEditList.candidateEmail !== candidateDataEditList.candidateEmail) {
            editObjectInOnversion.push('Candidate Email');
          }
          if (candidateEditList.candidateContact !== candidateDataEditList.candidateContact) {
            editObjectInOnversion.push('Candidate ontact');
          }
          if (candidateEditList.positionId !== candidateDataEditList.positionId) {
            editObjectInOnversion.push('Job');
          }

          const editConversionListDes =
            editObjectInOnversion.length > 0 ? `EDITED ${editObjectInOnversion.toString()}` : '';

          avPayload.description = `${editConversionListDes} ${getSubmissonConversionUIName(
            avChangedValueViaDirtyFileds
          )} for Placement under Client`;
        } else {
          avPayload.description = `ADDED ${data.candidateName} for Placement under Client`;
        }
        avPayload.parentEntitiyType = LogContextType.Client;
        avPayload.parentEntitiyId = data.clientId;
      }
      if (response) {
        avPayload.entityType = LogEntityType.Placement;
        avPayload.field = `Placement`;
        avPayload.entityName = data.candidateName;
        avPayload.contextData = {
          clientId: data.clientId,
          clientContactId: data.clientContactId,
          currencyId: data.currencyId,
          feeArrangementId: data.feeArrangementId,
          ourEntityId: data.ourEntityId,
          positionId: data.positionId,
        };
        avChangedValueViaDirtyFileds[0].length > 0 ? await saveActivityLog(avPayload) : '';
        navigate('/placements/' + response?.data?.value, {
          state: { placementType: placementType },
        });
      }
      setNotification(success(`Placement details successfully saved.`));
    } catch (error) {
      setNotification(failed(`Something went wrong while saving placement details.`));
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box borderRadius={1.5}>
            <Grid container py={1}>
              <Grid item p={1} sm={5.75}>
                <Label>Account Manager</Label>
                <RecruiterSuggestions
                  name="accountManager"
                  multiple={false}
                  requireRecruiter={true}
                  placeholder="Search account manager"
                  defaultValue={
                    methods.getValues('accountManager')
                      ? [methods.getValues('accountManager')['text']]
                      : []
                  }
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <Label>Team Leader</Label>
                <RecruiterSuggestions
                  name="teamLeader"
                  multiple={false}
                  requireRecruiter={true}
                  placeholder="Search team leader"
                  defaultValue={
                    methods.getValues('teamLeader') ? [methods.getValues('teamLeader')['text']] : []
                  }
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <Label>Recruiter 1</Label>
                <RecruiterSuggestions
                  name="recruiterOne"
                  multiple={false}
                  requireRecruiter={true}
                  placeholder="Search Recruiter"
                  defaultValue={
                    methods.getValues('recruiterOne')
                      ? [methods.getValues('recruiterOne')['text']]
                      : []
                  }
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <Label>Recruiter 2</Label>
                <RecruiterSuggestions
                  name="recruiterTwo"
                  multiple={false}
                  requireRecruiter={true}
                  placeholder="Search Recruiter"
                  defaultValue={
                    methods.getValues('recruiterTwo')
                      ? [methods.getValues('recruiterTwo')['text']]
                      : []
                  }
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <Label>Delivery Manager</Label>
                <RecruiterSuggestions
                  name="deliveryManager"
                  multiple={false}
                  requireRecruiter={true}
                  placeholder="Search delivery manager"
                  defaultValue={
                    methods.getValues('deliveryManager')
                      ? [methods.getValues('deliveryManager')['text']]
                      : []
                  }
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <Label>Delivery Lead</Label>
                <RecruiterSuggestions
                  name="deliveryLead"
                  multiple={false}
                  requireRecruiter={true}
                  placeholder="Search delivery lead"
                  defaultValue={
                    methods.getValues('deliveryLead')
                      ? [methods.getValues('deliveryLead')['text']]
                      : []
                  }
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ClientContactAutoComplete
                  key={clientRecruiterOne}
                  label="Client Contact 1"
                  name="clientRecruiterOne"
                  clientId={methods.getValues('clientId')}
                  disabled={false}
                  textForAddingNew="Client Contact"
                  clientName={companyName}
                  clientContactIdFieldName="clientRecruiterIdOne"
                  placeholder="Search client contact"
                  onNewValueCreate={(data: string) => {
                    setValue('clientRecruiterOne', '');
                    setCompanyContactInput(data);
                    setOpenClientContactDialog(true);
                  }}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ClientContactAutoComplete
                  key={clientRecruiterTwo}
                  label="Client Contact 2"
                  name="clientRecruiterTwo"
                  clientId={methods.getValues('clientId')}
                  disabled={false}
                  textForAddingNew="Client Contact"
                  clientName={companyName}
                  clientContactIdFieldName="clientRecruiterIdTwo"
                  placeholder="Search client contact"
                  onNewValueCreate={(data: string) => {
                    setValue('clientRecruiterTwo', '');
                    setCompanyContactInput(data);
                    setOpenClientContactDialog(true);
                  }}
                />
              </Grid>
              <Grid item p={1} sm={11.5}>
                <ControlledTextArea name="comments" labelText="Comments" minRows={8} />
              </Grid>
            </Grid>
          </Box>
          <Stack
            p={1}
            spacing={2}
            direction="row"
            sx={{ display: 'flex', justifyContent: 'right' }}
          >
            <WsButton variant="outlined" size="medium" type="button" onClick={onCancel}>
              {`Cancel`}
            </WsButton>
            <WsButton variant="contained" size="medium" type="submit">
              {`Add Placement`}
            </WsButton>
          </Stack>
        </form>
      </FormProvider>
      {openClientContactDialog && companyName && (
        <CreateNewClientContactDialog
          open={openClientContactDialog}
          clientId={methods.getValues('clientId')}
          clientName={companyName ?? ''}
          clientRecruiterEmailInputVal={companyContactInput ?? ''}
          onClose={() => setOpenClientContactDialog(false)}
          onSelect={onClientContactCreateFromDialog}
        />
      )}
    </>
  );
};

export default ClosureRecruiterDetails;
